/**
 * Cart page controls — Figma "Cart V1" (4437:2347).
 *
 * assets/cart.js already owns the AJAX layer, the request queue and the
 * <cart-page> re-render, and it delegates quantity links, remove links and
 * add-to-cart forms. Only the controls the design adds on top of that need
 * script, so that is all this file defines.
 *
 * Every element here is re-created whenever cart.js swaps <cart-page>'s
 * innerHTML, which is why timers are started in connectedCallback and cleared
 * in disconnectedCallback.
 *
 * Subscribe / one-time switching is not here: the drawer needs the same control
 * on every page, so it lives in component-cart-purchase-mode.js and both load it.
 */

/* ---------------------------------------------------------------------------
   Ship-by promise countdown
   The seed is rendered in Liquid so it reflects the shop's timezone; this only
   counts it down.
--------------------------------------------------------------------------- */

if (!customElements.get('cart-ship-promise')) {
  customElements.define(
    'cart-ship-promise',
    class CartShipPromise extends HTMLElement {
      connectedCallback() {
        this.output = this.querySelector('[data-countdown]');
        this.window = this.querySelector('[data-window]');
        this.remaining = parseInt(this.dataset.remaining, 10) || 0;
        if (!this.output || this.remaining <= 0) return;

        this.render();
        this.timer = setInterval(() => {
          this.remaining -= 1;
          if (this.remaining <= 0) {
            clearInterval(this.timer);
            // Past the cutoff the server copy is stale — ask for fresh markup.
            window.Cart?.refresh();
            return;
          }
          this.render();
        }, 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      render() {
        const hours = Math.floor(this.remaining / 3600);
        const minutes = Math.floor((this.remaining % 3600) / 60);
        const seconds = this.remaining % 60;
        const pad = (value) => String(value).padStart(2, '0');
        this.output.textContent = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        if (this.window) this.window.textContent = `${hours}h ${minutes}m`;
      }
    },
  );
}

/* ---------------------------------------------------------------------------
   Cross-sell card — option chips retarget the card's hidden variant input
--------------------------------------------------------------------------- */

if (!customElements.get('cart-cross-sell-card')) {
  customElements.define(
    'cart-cross-sell-card',
    class CartCrossSellCard extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      onClick(event) {
        const option = event.target.closest('.cart-stack__option');
        if (!option || option.disabled || option.dataset.available === 'false') return;

        for (const chip of this.querySelectorAll('.cart-stack__option')) {
          chip.setAttribute('aria-pressed', String(chip === option));
        }

        const input = this.querySelector('[data-cross-sell-variant]');
        if (input) input.value = option.dataset.variantId;

        const price = this.querySelector('[data-cross-sell-price]');
        if (price && option.dataset.variantPrice) {
          price.textContent = option.dataset.variantPrice;
        }

        this.syncAddButton(option);
      }

      /**
       * The button is rendered for the variant selected on the server. Picking
       * another size retargets it: in cart, sold out, or Add.
       * @param {HTMLElement} option
       */
      syncAddButton(option) {
        const button = this.querySelector('.cart-stack__add');
        const text = button?.querySelector('[data-atc-text]');
        if (!button || !text) return;

        const inCart = option.dataset.inCart === 'true';
        const available = option.dataset.available !== 'false';

        if (inCart) {
          button.disabled = true;
          text.textContent = button.dataset.labelInCart || text.textContent;
          return;
        }

        if (!available) {
          button.disabled = true;
          text.textContent = button.dataset.labelSoldOut || text.textContent;
          return;
        }

        button.disabled = false;
        text.textContent = button.dataset.labelAdd || text.textContent;
      }
    },
  );
}
